"use client";

import { useMemo, useRef, useState } from "react";
import { DndContext, type DragEndEvent, closestCenter } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button, SubmitButton } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@/components/ui/modal";
import { SkeletonBox, SkeletonLabel } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableEmptyOverlay,
  TableFillerRow,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
  TableSkeletonBody,
  useTableSkeletonCount,
} from "@/components/ui/table";
import { Toolbar } from "@/components/ui/toolbar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ApiError } from "@/lib/api/error";
import { useGetJob } from "@/lib/api/generated";
import type { ScorecardDefinitionOut } from "@/lib/api/model";
import { cn } from "@/lib/utils";

import {
  useAddFromLibrary,
  useCreateDefinition,
  useCustomizeDefinitions,
  useJobScorecardDefinitions,
  useReorderDefinitions,
  useUpdateDefinition,
} from "../hooks/use-definitions";
import { summarizeRule } from "../lib/rule-summary";
import type { SelectionRule } from "../lib/types";
import { ArchiveConfirmModal } from "./archive-confirm-modal";
import { type DefinitionPayload, ScorecardDefinitionSheet, toCreateBody, toPatchBody } from "./editor/definition-sheet";
import { LibraryPicker } from "./library-picker";
import {
  ArchivedBadge,
  DragHandle,
  LibraryUpdateBadge,
  RowIconButton,
  SourceBadge,
  fieldCountLabel,
  shortDate,
} from "./shared";
import { useScorecardSensors } from "./sortable";

const NAME_COL = "w-[30%] min-w-0";
const SOURCE_COL = "w-[18%] min-w-0";
const RULE_COL = "w-[24%] min-w-0";
const UPDATED_COL = "w-[14%] whitespace-nowrap";
const ACTIONS_COL = "w-[14%]";
const ROW_CELL = "!h-14 py-0";
const COLUMN_COUNT = 5;

export function JobScorecardsTab({ jobId }: { jobId: string }) {
  const job = useGetJob(jobId, { query: { enabled: Boolean(jobId) } });
  const stages = useMemo(
    () => (job.data?.stages ?? []).map((s: { id: string; name: string }) => ({ id: s.id, name: s.name })),
    [job.data],
  );
  const [showArchived, setShowArchived] = useState(false);
  const set = useJobScorecardDefinitions(jobId, showArchived);
  const customize = useCustomizeDefinitions(jobId);
  const create = useCreateDefinition(jobId);
  const addFromLib = useAddFromLibrary(jobId);
  const update = useUpdateDefinition(jobId);
  const reorder = useReorderDefinitions(jobId);
  const sensors = useScorecardSensors();
  const tableRef = useRef<HTMLDivElement>(null);
  const emptyRef = useRef<HTMLDivElement>(null);
  const skeletonCount = useTableSkeletonCount(tableRef, { rowHeight: 56 });
  const emptySkeletonCount = useTableSkeletonCount(emptyRef, { rowHeight: 56 });

  const [sheet, setSheet] = useState<{ open: boolean; definition?: ScorecardDefinitionOut }>({ open: false });
  const [picker, setPicker] = useState(false);
  const [confirmCustomize, setConfirmCustomize] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<ScorecardDefinitionOut | null>(null);
  const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, string> | undefined>();

  const from = set.data?.resolved_from;
  const canEdit = set.data?.can_edit ?? false;
  const definitions = set.data?.definitions ?? [];
  const isDefault = from === "org_default";
  const canAdd = Boolean(canEdit && from && from !== "org_default");
  const busy =
    customize.isPending || create.isPending || addFromLib.isPending || update.isPending || reorder.isPending;
  const active = definitions.filter((d) => !d.archived_at);
  const archived = definitions.filter((d) => d.archived_at);
  // Archived types stay out of the list until "Show archived" is on.
  const visible = showArchived ? [...active, ...archived] : active;
  const loading = set.isLoading && !set.data;
  const loadError = set.isError && !set.data;

  const closeSheet = () => {
    setServerFieldErrors(undefined);
    // Keep `definition` while the sheet animates out so the form doesn't remount mid-exit.
    setSheet((s) => ({ ...s, open: false }));
  };

  const onSubmit = async (p: DefinitionPayload) => {
    setServerFieldErrors(undefined);
    try {
      if (sheet.definition) {
        await update.mutateAsync({ definitionId: sheet.definition.id, body: toPatchBody(p) });
      } else {
        await create.mutateAsync(toCreateBody(p));
      }
      closeSheet();
    } catch (err) {
      if (err instanceof ApiError && typeof err.detail?.field_key === "string") {
        setServerFieldErrors({ [err.detail.field_key]: err.message });
      }
      /* toasted by the hook */
    }
  };

  const onDragEnd = (e: DragEndEvent) => {
    if (!canAdd || reorder.isPending || !e.over || e.active.id === e.over.id) return;
    const ids = active.map((d) => d.id);
    const next = arrayMove(ids, ids.indexOf(String(e.active.id)), ids.indexOf(String(e.over.id)));
    reorder.mutate({ ids: next });
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-surface-primary">
      <Toolbar.Container className="justify-between">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-sm font-medium text-heading">Scorecard types</span>
          {set.data && (
            <Badge variant="outline-muted" size="sm" className="rounded-sm">
              {active.length}
            </Badge>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="How scorecard types are picked"
                className="flex size-5 cursor-help items-center justify-center rounded-md text-contrast/40 transition-colors hover:text-contrast/70"
              >
                <Icon name="IconQuestionmarkCircle2" size={15} aria-hidden="true" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-[18rem]">
              Each conversation picks one type — by these rules, or by hand. One scorecard per candidate, per type,
              per interviewer, per interview.
            </TooltipContent>
          </Tooltip>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {canEdit && (
            <>
              <label className="flex cursor-pointer items-center gap-1 text-[13px] text-contrast/60">
                <Switch
                  checked={showArchived}
                  onCheckedChange={setShowArchived}
                  aria-label="Show archived"
                />
                Show archived
              </label>
              <Toolbar.Separator />
            </>
          )}
          {canEdit && isDefault && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setConfirmCustomize(true)}
              disabled={busy}
              icon={<Icon name="IconPencil" size={15} />}
            >
              Customize for this job
            </Button>
          )}
          {canEdit && (
            <Button
              variant="secondary"
              size="sm"
              disabled={!canAdd || busy}
              title={isDefault ? "Customize first" : undefined}
              onClick={() => setPicker(true)}
              icon={<Icon name="IconClipboard" size={15} />}
            >
              Add from library
            </Button>
          )}
          {canEdit && (
            <Button
              variant="light"
              size="sm"
              disabled={!canAdd || busy}
              title={isDefault ? "Customize first" : undefined}
              onClick={() => setSheet({ open: true })}
              icon={<Icon name="IconPlusSmall" size={15} />}
            >
              New type
            </Button>
          )}
        </div>
      </Toolbar.Container>

      {isDefault && (
        <div className="border-b border-border px-4 py-3">
          <Alert.Container role="status" dir="vertical" size="sm">
            <Alert.Title>Using the org default set</Alert.Title>
            <Alert.Content>
              Customize to give this job its own set — the default types are copied over and library updates are
              still offered.
            </Alert.Content>
          </Alert.Container>
        </div>
      )}

      {loadError ? (
        <div className="p-4">
          <Alert.Container variant="destructive">
            <Alert.Icon>
              <Icon name="IconExclamationCircle" size={16} aria-hidden="true" />
            </Alert.Icon>
            <Alert.Content>Could not load scorecard types.</Alert.Content>
          </Alert.Container>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={onDragEnd}
        >
          <div ref={tableRef} className="flex min-h-64 flex-1 flex-col overflow-y-auto overflow-x-hidden">
            {loading ? (
              <Table variant="list" layout="fixed">
                <JobScorecardsTHead />
                <JobScorecardsSkeletonRows count={skeletonCount} />
              </Table>
            ) : visible.length === 0 ? (
              <TableEmptyOverlay
                containerRef={emptyRef}
                icon="IconListSparkle"
                title={
                  archived.length > 0 && !showArchived
                    ? "All scorecard types are archived."
                    : "No scorecards yet"
                }
                description={
                  archived.length > 0 && !showArchived
                    ? "Turn on Show archived to restore one."
                    : canAdd
                      ? "Add one from the library or create a new type for this job."
                      : "Set up the org scorecard library, or customize this job to give it its own set."
                }
                backdrop={
                  <Table variant="list" layout="fixed">
                    <JobScorecardsTHead />
                    <JobScorecardsSkeletonRows count={emptySkeletonCount} />
                  </Table>
                }
              />
            ) : (
              <Table variant="list" layout="fixed">
                <JobScorecardsTHead sticky />
                <TableBody>
                  <SortableContext items={active.map((d) => d.id)} strategy={verticalListSortingStrategy}>
                    {visible.map((d) => (
                      <Row
                        key={d.id}
                        d={d}
                        sortable={canAdd}
                        stages={stages}
                        onOpen={() => setSheet({ open: true, definition: d })}
                        onArchive={canAdd ? () => setArchiveTarget(d) : undefined}
                        onRestore={
                          canEdit
                            ? () =>
                                update.mutate({
                                  definitionId: d.id,
                                  body: { archived: false },
                                  successMessage: "Scorecard type restored.",
                                })
                            : undefined
                        }
                        restoreDisabled={isDefault || busy}
                        restoreTitle={isDefault ? "Customize first" : "Restore"}
                      />
                    ))}
                  </SortableContext>
                  <TableFillerRow columns={COLUMN_COUNT} />
                </TableBody>
              </Table>
            )}
          </div>
        </DndContext>
      )}

      {showArchived && archived.length === 0 && !loadError && (
        <p className="border-t border-border px-4 py-2 text-[13px] text-contrast/50">No archived types.</p>
      )}

      <TablePagination
        page={1}
        total={visible.length}
        perPage={Math.max(visible.length, 1)}
        onPageChange={() => {}}
        disabled
        label={(t) => `${t} type${t !== 1 ? "s" : ""}`}
      />

      <ScorecardDefinitionSheet
        open={sheet.open}
        onOpenChange={(o) => {
          if (!o) setServerFieldErrors(undefined);
          setSheet((s) => ({ ...s, open: o }));
        }}
        mode="job"
        definition={sheet.definition}
        stages={stages}
        onSubmit={onSubmit}
        busy={busy}
        readOnly={!canAdd || Boolean(sheet.definition?.archived_at)}
        serverFieldErrors={serverFieldErrors}
        onApplyLibraryUpdate={
          sheet.definition
            ? async () => {
                try {
                  await update.mutateAsync({
                    definitionId: sheet.definition!.id,
                    body: { apply_library_update: true },
                  });
                  closeSheet();
                } catch {
                  /* toasted by the hook */
                }
              }
            : undefined
        }
      />
      <LibraryPicker
        open={picker}
        onOpenChange={setPicker}
        takenNames={active.map((d) => d.name)}
        busy={addFromLib.isPending}
        onPick={async (id) => {
          try {
            await addFromLib.mutateAsync({ library_definition_id: id });
            setPicker(false);
          } catch {
            /* toasted by the hook */
          }
        }}
      />

      <Modal open={confirmCustomize} onOpenChange={setConfirmCustomize}>
        <ModalContent size="sm">
          <ModalHeader title="Customize scorecards for this job?" iconName="IconPencil" />
          <ModalBody className="p-5">
            <p className="text-sm text-contrast/60">
              Copies the {active.length} default types onto this job so you can add, remove, and reorder them.
              Library updates are still offered.
            </p>
          </ModalBody>
          <ModalFooter className="px-5">
            <Button variant="secondary" size="sm" onClick={() => setConfirmCustomize(false)}>
              Cancel
            </Button>
            <SubmitButton
              type="button"
              variant="light"
              size="sm"
              loading={customize.isPending}
              loadingLabel="Customizing"
              onClick={async () => {
                try {
                  await customize.mutateAsync();
                  setConfirmCustomize(false);
                } catch {
                  /* toasted by the hook */
                }
              }}
            >
              Customize
            </SubmitButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ArchiveConfirmModal
        target={archiveTarget}
        body="Existing scorecards keep it; it stops being offered."
        pending={update.isPending}
        onCancel={() => setArchiveTarget(null)}
        onConfirm={async () => {
          if (!archiveTarget) return;
          try {
            await update.mutateAsync({
              definitionId: archiveTarget.id,
              body: { archived: true },
              successMessage: "Scorecard type archived.",
            });
            setArchiveTarget(null);
          } catch {
            /* toasted by the hook */
          }
        }}
      />
    </div>
  );
}

function JobScorecardsTHead({ sticky }: { sticky?: boolean }) {
  return (
    <TableHeader sticky={sticky}>
      <TableRow>
        <TableHead className={NAME_COL}>
          <span className="inline-flex items-center gap-1">
            <Icon name="IconChecklist" size={14} aria-hidden="true" className="text-muted-foreground" />
            Type
          </span>
        </TableHead>
        <TableHead className={SOURCE_COL}>Source</TableHead>
        <TableHead className={RULE_COL}>
          <span className="inline-flex items-center gap-1">
            <Icon name="IconTarget" size={14} aria-hidden="true" className="text-muted-foreground" />
            Default for
          </span>
        </TableHead>
        <TableHead className={UPDATED_COL}>
          <span className="inline-flex items-center gap-1">
            <Icon name="IconClock" size={14} aria-hidden="true" className="text-muted-foreground" />
            Updated
          </span>
        </TableHead>
        <TableHead className={ACTIONS_COL} aria-label="Actions" />
      </TableRow>
    </TableHeader>
  );
}

/** Skeleton twin of `Row` — same column widths and 56px row height. */
function JobScorecardsSkeletonRows({ count }: { count: number }) {
  return (
    <TableSkeletonBody
      count={count}
      cellClassName={ROW_CELL}
      renderCells={() => [
        <div key="name" className="flex min-w-0 flex-col gap-1.5">
          <SkeletonLabel className="w-36" />
          <SkeletonLabel tone="muted" className="w-16" />
        </div>,
        <SkeletonBox key="source" tone="muted" className="h-5 w-20 rounded-sm" />,
        <SkeletonLabel key="rule" tone="muted" className="w-28" />,
        <SkeletonLabel key="updated" tone="muted" className="w-20" />,
        <SkeletonBox key="actions" tone="muted" className="ml-auto h-4 w-10" />,
      ]}
    />
  );
}

function Row({
  d,
  sortable,
  stages,
  onOpen,
  onArchive,
  onRestore,
  restoreDisabled,
  restoreTitle,
}: {
  d: ScorecardDefinitionOut;
  sortable: boolean;
  stages: { id: string; name: string }[];
  onOpen: () => void;
  onArchive?: () => void;
  onRestore?: () => void;
  restoreDisabled?: boolean;
  restoreTitle?: string;
}) {
  const isArchived = Boolean(d.archived_at);
  const canDrag = sortable && !isArchived;
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition } = useSortable({
    id: d.id,
    disabled: !canDrag,
  });
  // Org-default rows (job_id null) are library rows shown directly; job rows are
  // "This job" unless copied from the library (then customized or not).
  const source =
    d.job_id == null
      ? "Library"
      : !d.library_origin
        ? "This job"
        : d.library_customized
          ? "Library (customized)"
          : "Library";
  const updated = shortDate(d.updated_at);

  return (
    <TableRow
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      interactive
      rowLabel={d.name}
      onClick={onOpen}
      className={isArchived ? "opacity-60" : undefined}
    >
      <TableCell className={cn(NAME_COL, ROW_CELL)}>
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 items-center gap-1.5">
            <Icon
              name="IconChecklist"
              size={14}
              aria-hidden="true"
              className="shrink-0 text-heading opacity-50"
            />
            <span className="min-w-0 truncate text-sm text-contrast/80">{d.name}</span>
            {isArchived && <ArchivedBadge />}
            {d.library_update_available && <LibraryUpdateBadge />}
          </div>
          <span className="truncate text-[13px] text-contrast/50">
            {fieldCountLabel(d.current_version.fields.length)}
          </span>
        </div>
      </TableCell>

      <TableCell className={cn(SOURCE_COL, ROW_CELL)}>
        <SourceBadge source={source} />
      </TableCell>

      <TableCell className={cn(RULE_COL, ROW_CELL)}>
        <span className="block truncate text-[13px] text-contrast/60">
          {summarizeRule(d.selection_rule as SelectionRule | null, stages)}
        </span>
      </TableCell>

      <TableCell className={cn(UPDATED_COL, ROW_CELL)}>
        <span className="text-sm text-contrast/60">{updated ?? "—"}</span>
      </TableCell>

      <TableCell
        className={cn(ACTIONS_COL, ROW_CELL, "last:px-3")}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-end gap-0.5">
          {canDrag && (
            <DragHandle
              ref={setActivatorNodeRef}
              label={`Drag to reorder ${d.name}`}
              title="Drag to reorder"
              {...attributes}
              {...listeners}
            />
          )}
          {isArchived
            ? onRestore && (
                <RowIconButton
                  label="Restore"
                  title={restoreTitle}
                  disabled={restoreDisabled}
                  onClick={onRestore}
                >
                  <Icon name="IconArrowRotateClockwise" size={15} aria-hidden="true" />
                </RowIconButton>
              )
            : onArchive && (
                <RowIconButton label="Archive" title="Archive" onClick={onArchive}>
                  <Icon name="IconArchive" size={15} aria-hidden="true" />
                </RowIconButton>
              )}
        </div>
      </TableCell>
    </TableRow>
  );
}
